import {
  DestroyRef,
  EnvironmentInjector,
  InjectionToken,
  inject,
  runInInjectionContext,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { timer } from "rxjs";
import { environment } from "../../../environments/environment";
import { COMPILATION_CONFIG } from "./compilation-config.token";
import { specificConfig } from "./config-specific-bot";
import { AppConfig, RunningConfig } from "./config.model";
import { BackendConfigApiClient } from "./connector/backend-config.apiclient";

/**
 * Launches an asynchronous task that will retrieve the backend config and update the passed config
 * with the data retrieved from the server
 */
function asyncRetrieveBackendConfig(config: AppConfig) {
  const environmentInjector = inject(EnvironmentInjector);
  const destroyRef = inject(DestroyRef);
  timer(0)
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(() =>
      runInInjectionContext(environmentInjector, () => {
        const apiClient = inject(BackendConfigApiClient);
        apiClient.retrieveConfig(config.backendBaseUrl + "ui-config/").subscribe({
          next: (backendConfig) => {
            if (config.headerBanner) {
              config.headerBanner.text.set(backendConfig.banner_txt);
            }
          },
          error: (err) => console.error(`Unable to retrieve backend config, ${err}`),
        });
      })
    );
}

function convertToExpectedType(config: Partial<RunningConfig>) {
  /*Convert boolean*/
  if (config) {
    for (const [key, value] of Object.entries(config)) {
      if (value === "True" || value === "true") {
        (config[key as keyof RunningConfig] as boolean) = true;
      } else if (value === "False" || value === "false") {
        (config[key as keyof RunningConfig] as boolean) = false;
      }
    }
  }
  /*Convert numbers*/
  if (config?.delayInConversationMessagesDisplayInMs) {
    config["delayInConversationMessagesDisplayInMs"] = Number(
      config.delayInConversationMessagesDisplayInMs
    );
  }
  return config;
}

export const APP_CONFIG = new InjectionToken<AppConfig>("app.config", {
  factory: () => {
    // Get compilation config from event-bus init
    const compilationConfig = inject(COMPILATION_CONFIG);

    // Get running config for production from docker-entrypoint
    const prodRunningConfig = convertToExpectedType(
      // Variable d'environnement en production
      (globalThis as any)["BOT_WEB_INTERFACE_SETTINGS"]
    );

    const config: AppConfig = {
      ...environment, // environnment is the common running config + production status (same for all bots)
      ...compilationConfig, // compilation config from loader-factory
      ...specificConfig[compilationConfig.botName], // specific config for the running bot
      ...prodRunningConfig, //overwrite with production env variables.
    };
    // The loader already merged the env `auth` override over the compiled one; keep that merged value
    // instead of the partial env object spread above.
    if (compilationConfig.auth) {
      config.auth = compilationConfig.auth;
    }
    if (config.hasConfigurationFromBackend) {
      asyncRetrieveBackendConfig(config);
    }
    return config;
  },
});

export { AppConfig, AuthType, HeaderActionType } from "./config.model";
